import type { ChatMessageRow, DocumentRow, JsonRecord } from "@/lib/api";
import type { CitationObject, EditAnnotation, ResidualFlag, WebCitationObject } from "@/lib/stream";

export type AssistantEventType =
  | "content"
  | "reasoning"
  | "thinking"
  | "tool_call_start"
  | "doc_read"
  | "doc_find"
  | "doc_created"
  | "doc_download"
  | "doc_replicated"
  | "doc_edited"
  | "web_search"
  | "corpus_search"
  | "workflow"
  | "review"
  | "research_report";

export interface BaseAssistantEvent extends JsonRecord {
  id: string;
  type: AssistantEventType;
  isStreaming?: boolean;
}

export interface ContentEvent extends BaseAssistantEvent {
  type: "content";
  text: string;
}

export interface ReasoningEvent extends BaseAssistantEvent {
  type: "reasoning";
  text: string;
}

export interface ThinkingEvent extends BaseAssistantEvent {
  type: "thinking";
}

export interface ToolCallStartEvent extends BaseAssistantEvent {
  type: "tool_call_start";
  name: string;
}

export interface DocReadEvent extends BaseAssistantEvent {
  type: "doc_read";
  filename: string;
  document_id?: string | null;
}

export interface DocFindEvent extends BaseAssistantEvent {
  type: "doc_find";
  filename: string;
  query: string;
  total_matches?: number;
}

export interface DocCreatedEvent extends BaseAssistantEvent {
  type: "doc_created";
  filename: string;
  download_url?: string;
  document_id?: string;
  version_id?: string;
  version_number?: number | null;
}

export interface DocDownloadEvent extends BaseAssistantEvent {
  type: "doc_download";
  filename: string;
  download_url: string;
}

export interface DocReplicatedEvent extends BaseAssistantEvent {
  type: "doc_replicated";
  filename: string;
  count: number;
  copies?: Array<{ new_filename: string; document_id: string; version_id: string }>;
  error?: string;
}

export interface DocEditedEvent extends BaseAssistantEvent {
  type: "doc_edited";
  filename: string;
  document_id?: string;
  version_id?: string;
  version_number?: number | null;
  download_url?: string;
  annotations?: EditAnnotation[];
  error?: string;
}

export interface WebSearchEvent extends BaseAssistantEvent {
  type: "web_search";
  query: string;
  num_results?: number;
}

export interface CorpusSearchEvent extends BaseAssistantEvent {
  type: "corpus_search";
  query: string;
  num_results?: number;
}

export interface WorkflowEvent extends BaseAssistantEvent {
  type: "workflow";
  status: "running" | "done" | "budget_exhausted";
  name?: string;
  phase?: string;
  /** Human-readable stage label for drafting workflows (e.g. "Drafting sections (2/5)"). */
  label?: string;
  phaseCount?: number;
  numTasks?: number;
  completed?: number;
  kept?: number;
  refuted?: number;
  started?: number;
  /** True when a drafting workflow ended in failure (draft_failed). */
  error?: boolean;
  inFlight: string[];
}

export interface ReportSpan extends JsonRecord {
  quote: string;
  locator?: { kind?: string; page?: number; char_start?: number; char_end?: number };
  document_id?: string;
  version_id?: string;
  version_number?: number | null;
  filename?: string;
}

export interface ReportVerification extends JsonRecord {
  status: "verified" | "unverified" | "unchecked";
  fidelity?: string;
  judged?: boolean;
  reason?: string;
  repaired?: boolean;
}

export interface ReportClaim extends JsonRecord {
  text: string;
  spans: ReportSpan[];
  verification: ReportVerification;
}

export interface ReportSection extends JsonRecord {
  heading: string;
  claims: ReportClaim[];
}

export interface StructuredReport extends JsonRecord {
  sections: ReportSection[];
}

export interface ResearchReportEvent extends BaseAssistantEvent {
  type: "research_report";
  report: StructuredReport;
}

export interface ClaimVerdict extends JsonRecord {
  ref: number;
  verdict: string;
  confidence: number;
}

/**
 * Folded attribution-guard "checking" panel (Spec 1b). The backend emits a
 * stream of review_started / claim_verdict / redraft_started / review_done
 * events; the timeline shows one row that updates in place via foldReviewEvent.
 */
export interface ReviewEvent extends BaseAssistantEvent {
  type: "review";
  status: "running" | "done";
  claimCount?: number;
  checked?: number;
  corrected?: number;
  redrafting?: boolean;
  verdicts?: ClaimVerdict[];
  residualFlags?: ResidualFlag[];
}

export type AssistantEvent =
  | ContentEvent
  | ReasoningEvent
  | ThinkingEvent
  | ToolCallStartEvent
  | DocReadEvent
  | DocFindEvent
  | DocCreatedEvent
  | DocDownloadEvent
  | DocReplicatedEvent
  | DocEditedEvent
  | WebSearchEvent
  | CorpusSearchEvent
  | WorkflowEvent
  | ReviewEvent
  | ResearchReportEvent;

export interface AssistantChatMessage {
  id: string;
  role: "user" | "assistant";
  content?: string;
  files?: AttachedDocument[];
  events?: AssistantEvent[];
  annotations?: Array<CitationObject | EditAnnotation | WebCitationObject>;
  isStreaming?: boolean;
  error?: string;
  created_at?: string;
  duration_ms?: number;
}

export interface AttachedDocument {
  /** Raw filename sent to the backend as the document reference. */
  filename: string;
  /** Clean name (storage hash stripped) for display only; falls back to filename. */
  displayName?: string;
  document_id: string;
}

export interface ChatInputPayload {
  content: string;
  attachedDocuments: AttachedDocument[];
  webSearchEnabled?: boolean;
  workflowEnabled?: boolean;
}

/**
 * Fold a raw `workflow_*` SSE event into a single collapsed WorkflowEvent.
 *
 * The backend emits a stream of fine-grained workflow events (started, phase,
 * per-task started/done/refuted, progress, done). The timeline shows one row
 * that updates in place, so both the live hook and stored-history normalizer
 * reduce that stream onto a single WorkflowEvent through this helper.
 */
export function foldWorkflowEvent(prev: WorkflowEvent | null, raw: Record<string, unknown>): WorkflowEvent {
  const num = (value: unknown) => (typeof value === "number" ? value : undefined);
  const str = (value: unknown) => (typeof value === "string" ? value : undefined);
  const next: WorkflowEvent = prev
    ? { ...prev, inFlight: [...prev.inFlight] }
    : { id: "", type: "workflow", status: "running", isStreaming: true, completed: 0, kept: 0, refuted: 0, started: 0, inFlight: [] };

  switch (raw.type) {
    case "workflow_started":
      next.name = str(raw.name) ?? next.name;
      next.numTasks = num(raw.num_tasks) ?? next.numTasks;
      next.status = "running";
      next.isStreaming = true;
      break;
    case "workflow_phase": {
      next.phase = str(raw.task_id) ?? str(raw.phase) ?? next.phase;
      next.label = str(raw.label) ?? next.label;
      next.phaseCount = num(raw.count) ?? next.phaseCount;
      const phase = str(raw.phase);
      if (phase === "draft_ready" || phase === "draft_failed") {
        // Terminal drafting phases: keep accumulated counts for summary display.
        next.status = "done";
        next.isStreaming = false;
        next.inFlight = [];
        next.error = phase === "draft_failed";
      } else {
        next.completed = 0;
        next.started = 0;
        next.inFlight = [];
      }
      break;
    }
    case "task_started": {
      const item = str(raw.item);
      if (item && !next.inFlight.includes(item)) {
        next.inFlight = [...next.inFlight, item];
      }
      break;
    }
    case "task_done": {
      const item = str(raw.item);
      if (item) {
        next.inFlight = next.inFlight.filter((entry) => entry !== item);
      }
      break;
    }
    case "task_refuted": {
      const item = str(raw.item);
      next.refuted = (next.refuted ?? 0) + 1;
      if (item) {
        next.inFlight = next.inFlight.filter((entry) => entry !== item);
      }
      break;
    }
    case "workflow_progress":
      next.completed = num(raw.completed) ?? next.completed;
      next.kept = num(raw.kept) ?? next.kept;
      next.started = num(raw.started) ?? next.started;
      next.label = str(raw.label) ?? next.label;
      next.phaseCount = num(raw.total) ?? next.phaseCount;
      break;
    case "workflow_done":
      next.completed = num(raw.completed) ?? next.completed;
      next.kept = num(raw.kept) ?? next.kept;
      next.refuted = num(raw.refuted) ?? next.refuted;
      next.status = "done";
      next.isStreaming = false;
      next.inFlight = [];
      break;
    case "workflow_budget_exhausted":
      next.started = num(raw.started) ?? next.started;
      next.status = "budget_exhausted";
      next.isStreaming = false;
      next.inFlight = [];
      break;
    default:
      break;
  }
  return next;
}

/**
 * Fold a raw attribution-guard review event (`review_started`, `claim_verdict`,
 * `redraft_started`, `review_done`) into a single collapsed ReviewEvent. Mirrors
 * `foldWorkflowEvent`. `answer_revised` is handled separately (it swaps the shown
 * answer, not the panel).
 */
export function foldReviewEvent(prev: ReviewEvent | null, raw: Record<string, unknown>): ReviewEvent {
  const num = (value: unknown) => (typeof value === "number" ? value : undefined);
  const next: ReviewEvent = prev
    ? { ...prev, verdicts: [...(prev.verdicts ?? [])], residualFlags: [...(prev.residualFlags ?? [])] }
    : { id: "", type: "review", status: "running", isStreaming: true, verdicts: [], residualFlags: [] };

  switch (raw.type) {
    case "review_started":
      next.claimCount = num(raw.claim_count) ?? next.claimCount;
      next.status = "running";
      next.isStreaming = true;
      break;
    case "claim_verdict": {
      const ref = num(raw.ref);
      if (ref !== undefined) {
        const verdict: ClaimVerdict = { ref, verdict: String(raw.verdict ?? ""), confidence: num(raw.confidence) ?? 0 };
        const verdicts = (next.verdicts ?? []).filter((v) => v.ref !== ref);
        verdicts.push(verdict);
        next.verdicts = verdicts;
      }
      break;
    }
    case "redraft_started":
      next.redrafting = true;
      break;
    case "review_done":
      next.checked = num(raw.checked) ?? next.checked;
      next.corrected = num(raw.corrected) ?? next.corrected;
      next.residualFlags = Array.isArray(raw.residual_flags) ? (raw.residual_flags as ResidualFlag[]) : (next.residualFlags ?? []);
      next.redrafting = false;
      next.status = "done";
      next.isStreaming = false;
      break;
    default:
      break;
  }
  return next;
}

export interface OpenDocumentRequest {
  documentId: string;
  filename: string;
  versionId?: string;
  versionNumber?: number | null;
  kind?: "document" | "citation" | "edit";
  citation?: CitationObject;
  edit?: EditAnnotation;
}

export type DocumentPickerItem = Pick<DocumentRow, "id" | "filename" | "display_name" | "file_type" | "current_version_id" | "latest_version_number">;

function parseAttachedDocuments(value: JsonRecord[string]): AttachedDocument[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return [];
    }
    const record = item as JsonRecord;
    return typeof record.filename === "string" && typeof record.document_id === "string" ? [{ filename: record.filename, document_id: record.document_id }] : [];
  });
}

/**
 * Normalize raw SSE events stored in the DB into proper AssistantEvent objects.
 *
 * The backend stores raw SSE dicts (e.g. `content_delta`, `content_done`,
 * `reasoning_delta`, `reasoning_block_end`, `chat_id`, `citations`) but the
 * frontend expects collapsed types (`content`, `reasoning`, `tool_call_start`,
 * doc events). This function merges deltas and strips bookkeeping events so
 * that history loaded from the API renders identically to the live stream.
 */
function normalizeSseEvents(raw: JsonRecord[], messageId: string): AssistantEvent[] {
  const events: AssistantEvent[] = [];
  let contentBuf = "";
  let reasoningBuf = "";
  let workflow: WorkflowEvent | null = null;
  let review: ReviewEvent | null = null;
  let seq = 0;
  const eid = () => `${messageId}-ev-${seq++}`;

  // Pre-scan: build set of terminal event types so we can skip redundant
  // _start events when the completed counterpart is already stored.
  const terminalTypes = new Set(raw.map((item) => item.type as string).filter(Boolean));
  // Maps raw SSE start types to their raw SSE terminal types.
  // For most events these coincide with the frontend-native type (e.g. "doc_read"),
  // but web_search uses a distinct SSE terminal type ("web_search_done") that the
  // switch below normalises to the frontend-native "web_search".
  const START_TO_DONE: Record<string, string> = {
    doc_read_start: "doc_read",
    doc_find_start: "doc_find",
    doc_created_start: "doc_created",
    doc_replicate_start: "doc_replicated",
    doc_edited_start: "doc_edited",
    web_search_start: "web_search_done",
    corpus_search_start: "corpus_search_done",
  };

  const flushContent = () => {
    if (contentBuf) {
      events.push({ id: eid(), type: "content", text: contentBuf } as ContentEvent);
      contentBuf = "";
    }
  };
  const flushReasoning = () => {
    if (reasoningBuf) {
      events.push({ id: eid(), type: "reasoning", text: reasoningBuf } as ReasoningEvent);
      reasoningBuf = "";
    }
  };

  for (const item of raw) {
    const t = item.type as string | undefined;
    if (!t) continue;

    // Drop _start events whose terminal counterpart is present in the stored array.
    if (t in START_TO_DONE && terminalTypes.has(START_TO_DONE[t])) continue;

    switch (t) {
      case "content_delta":
        contentBuf += (typeof item.text === "string" ? item.text : "");
        break;
      case "content_done":
        flushContent();
        break;
      case "reasoning_delta":
        reasoningBuf += (typeof item.text === "string" ? item.text : "");
        break;
      case "reasoning_block_end":
        flushReasoning();
        break;
      case "tool_call_start":
        flushReasoning();
        flushContent();
        events.push({ id: eid(), type: "tool_call_start", name: typeof item.name === "string" ? item.name : "" } as ToolCallStartEvent);
        break;
      case "doc_read_start":
        flushContent();
        events.push({ id: eid(), type: "doc_read", filename: typeof item.filename === "string" ? item.filename : "", document_id: typeof item.document_id === "string" ? item.document_id : null, isStreaming: true } as DocReadEvent);
        break;
      case "doc_read":
        events.push({ id: eid(), type: "doc_read", filename: typeof item.filename === "string" ? item.filename : "", document_id: typeof item.document_id === "string" ? item.document_id : null } as DocReadEvent);
        break;
      case "doc_find_start":
        flushContent();
        events.push({ id: eid(), type: "doc_find", filename: typeof item.filename === "string" ? item.filename : "", query: typeof item.query === "string" ? item.query : "", isStreaming: true } as DocFindEvent);
        break;
      case "doc_find":
        events.push({ id: eid(), type: "doc_find", filename: typeof item.filename === "string" ? item.filename : "", query: typeof item.query === "string" ? item.query : "", total_matches: typeof item.total_matches === "number" ? item.total_matches : 0 } as DocFindEvent);
        break;
      case "doc_created_start":
        flushContent();
        events.push({ id: eid(), type: "doc_created", filename: typeof item.filename === "string" ? item.filename : "", isStreaming: true } as DocCreatedEvent);
        break;
      case "doc_created":
        events.push({
          id: eid(), type: "doc_created",
          filename: typeof item.filename === "string" ? item.filename : "",
          download_url: typeof item.download_url === "string" ? item.download_url : undefined,
          document_id: typeof item.document_id === "string" ? item.document_id : undefined,
          version_id: typeof item.version_id === "string" ? item.version_id : undefined,
          version_number: typeof item.version_number === "number" ? item.version_number : null,
        } as DocCreatedEvent);
        break;
      case "doc_download":
        events.push({ id: eid(), type: "doc_download", filename: typeof item.filename === "string" ? item.filename : "", download_url: typeof item.download_url === "string" ? item.download_url : "" } as DocDownloadEvent);
        break;
      case "doc_replicate_start":
        flushContent();
        events.push({ id: eid(), type: "doc_replicated", filename: typeof item.filename === "string" ? item.filename : "", count: typeof item.count === "number" ? item.count : 0, isStreaming: true } as DocReplicatedEvent);
        break;
      case "doc_replicated":
        events.push({
          id: eid(), type: "doc_replicated",
          filename: typeof item.filename === "string" ? item.filename : "",
          count: typeof item.count === "number" ? item.count : 0,
          copies: Array.isArray(item.copies) ? (item.copies as DocReplicatedEvent["copies"]) : undefined,
          error: typeof item.error === "string" ? item.error : undefined,
        } as DocReplicatedEvent);
        break;
      case "doc_edited_start":
        flushContent();
        events.push({ id: eid(), type: "doc_edited", filename: typeof item.filename === "string" ? item.filename : "", isStreaming: true } as DocEditedEvent);
        break;
      case "doc_edited":
        events.push({
          id: eid(), type: "doc_edited",
          filename: typeof item.filename === "string" ? item.filename : "",
          document_id: typeof item.document_id === "string" ? item.document_id : undefined,
          version_id: typeof item.version_id === "string" ? item.version_id : undefined,
          version_number: typeof item.version_number === "number" ? item.version_number : null,
          download_url: typeof item.download_url === "string" ? item.download_url : undefined,
          annotations: Array.isArray(item.annotations) ? (item.annotations as EditAnnotation[]) : undefined,
          error: typeof item.error === "string" ? item.error : undefined,
        } as DocEditedEvent);
        break;
      case "web_search_start":
        flushContent();
        events.push({ id: eid(), type: "web_search", query: typeof item.query === "string" ? item.query : "", isStreaming: true } as WebSearchEvent);
        break;
      case "web_search_done":
        events.push({ id: eid(), type: "web_search", query: typeof item.query === "string" ? item.query : "", num_results: typeof item.num_results === "number" ? item.num_results : 0 } as WebSearchEvent);
        break;
      case "corpus_search_start":
        flushContent();
        events.push({ id: eid(), type: "corpus_search", query: typeof item.query === "string" ? item.query : "", isStreaming: true } as CorpusSearchEvent);
        break;
      case "corpus_search_done":
        events.push({ id: eid(), type: "corpus_search", query: typeof item.query === "string" ? item.query : "", num_results: typeof item.num_results === "number" ? item.num_results : 0 } as CorpusSearchEvent);
        break;
      // Already a frontend-native type stored from a previous version:
      case "content":
      case "reasoning":
        events.push({ ...item, id: (item.id as string) ?? eid() } as AssistantEvent);
        break;
      case "workflow_started":
      case "workflow_phase":
      case "task_started":
      case "task_done":
      case "task_refuted":
      case "workflow_progress":
      case "workflow_done":
      case "workflow_budget_exhausted": {
        flushReasoning();
        flushContent();
        const folded = foldWorkflowEvent(workflow, item as Record<string, unknown>);
        if (workflow) {
          const index = events.indexOf(workflow);
          if (index >= 0) {
            events[index] = folded;
          } else {
            events.push(folded);
          }
        } else {
          folded.id = eid();
          events.push(folded);
        }
        workflow = folded;
        break;
      }
      case "review_started":
      case "claim_verdict":
      case "redraft_started":
      case "review_done": {
        flushReasoning();
        flushContent();
        const folded = foldReviewEvent(review, item as Record<string, unknown>);
        if (review) {
          const index = events.indexOf(review);
          if (index >= 0) {
            events[index] = folded;
          } else {
            events.push(folded);
          }
        } else {
          folded.id = eid();
          events.push(folded);
        }
        review = folded;
        break;
      }
      case "answer_revised": {
        // Silent in-place swap of the shown answer. Content was already flushed
        // by content_done, so rewrite the most recent content event's text.
        flushContent();
        const revised = typeof item.content === "string" ? item.content : "";
        for (let i = events.length - 1; i >= 0; i -= 1) {
          if (events[i].type === "content") {
            events[i] = { ...(events[i] as ContentEvent), text: revised } as ContentEvent;
            break;
          }
        }
        break;
      }
      case "research_report": {
        flushReasoning();
        flushContent();
        const raw = (item as Record<string, unknown>).report;
        const report = (typeof raw === "object" && raw !== null && !Array.isArray(raw) ? raw : {}) as StructuredReport;
        events.push({ id: eid(), type: "research_report", report } as ResearchReportEvent);
        break;
      }
      // Bookkeeping events — skip
      case "chat_id":
      case "citations":
      case "turn_done":
      case "turn_meta":
      case "turn_failed":
        break;
      default:
        break;
    }
  }
  flushReasoning();
  flushContent();
  return events;
}

export function rowToAssistantMessage(row: ChatMessageRow): AssistantChatMessage {
  const id = row.id;
  if (row.role === "user") {
    const objectContent = typeof row.content === "object" && row.content !== null && !Array.isArray(row.content) ? row.content : null;
    return {
      id,
      role: "user",
      content: typeof row.content === "string" ? row.content : typeof objectContent?.content === "string" ? objectContent.content : "",
      files: parseAttachedDocuments(objectContent?.files) ?? parseAttachedDocuments(objectContent?.attachedDocuments),
      created_at: row.created_at ?? undefined
    };
  }

  const rawEvents = Array.isArray(row.content) ? (row.content as JsonRecord[]) : undefined;
  const events = rawEvents ? normalizeSseEvents(rawEvents, id) : undefined;
  const textContent = typeof row.content === "string" ? row.content : "";
  const turnDoneRaw = rawEvents?.find((item) => item.type === "turn_done");
  const turnFailedRaw = rawEvents?.find((item) => item.type === "turn_failed");
  const duration_ms = typeof turnDoneRaw?.duration_ms === "number" ? turnDoneRaw.duration_ms : undefined;
  const failureMessage =
    turnFailedRaw && typeof turnFailedRaw.message === "string" ? turnFailedRaw.message : "Something went wrong. Please try again.";
  const resolvedEvents = events ?? (textContent ? [{ id: `${id}-content`, type: "content", text: textContent } as AssistantEvent] : []);
  // Defense-in-depth: a terminal 'done' turn whose stored events normalize to nothing
  // renderable (e.g. only denylisted error events) must surface as a failure, not an
  // empty bubble indistinguishable from a still-streaming turn.
  const isError = row.status === "error" || (row.status === "done" && resolvedEvents.length === 0 && !textContent);
  return {
    id,
    role: "assistant",
    content: events ? undefined : textContent,
    events: resolvedEvents,
    annotations: Array.isArray(row.annotations) ? (row.annotations as Array<CitationObject | EditAnnotation | WebCitationObject>) : [],
    error: isError ? failureMessage : undefined,
    created_at: row.created_at ?? undefined,
    duration_ms,
  };
}

export function isEditAnnotation(value: CitationObject | EditAnnotation | WebCitationObject): value is EditAnnotation {
  return "kind" in value && value.kind === "edit";
}
